import * as p from '@clack/prompts'
import { existsSync, readdirSync, realpathSync, rmSync } from 'node:fs'
import { isAbsolute, resolve } from 'node:path'
import { ensureToken } from '../../auth'
import { findAppDir } from '../../lib/app-context'
import { parseAppArg, resolveAppTarget } from '../../lib/app-target'
import { defineDeepspaceCommand, Refusal } from '../../lib/command'
import { runGit } from '../../lib/git/process'
import { listWorktrees, resolveCommit, switchToNewBranch } from '../../lib/git/repository'
import { isWorkspaceId, resolveWorkspaceWorktree, workspaceBranchName } from '../../lib/workspace-id'
import {
  deployBaseUrl,
  ensureSpaceRemote,
  runGitRemote,
  SPACE_REMOTE,
  spacePrivateRef,
} from '../../lib/vc-remote'
import { repoApi, type RemoteWorkspaceView } from '../../lib/repo-api'
import { createSpinner } from '../../lib/spinner'
import { errorCode } from '../../lib/cli-errors'
import type { CliAction } from '../../lib/output'
import { detectPackageManager } from '../../lib/package-manager'
import {
  appDirInWorktree,
  defaultWorkspaceRoot,
  materializeWorkspaceWorktree,
  rematerializeWorkspaceWorktree,
} from './local'
import { workspaceSyncRelation } from './analysis'
import { APP_ARG, resolveTarget } from './runtime'

/** Same directory, symlinks resolved — git canonicalizes worktree paths on
 *  every platform (macOS `/tmp/x` lists as `/private/tmp/x`; a Linux
 *  container's bind-mounted `/workspace` likewise resolves), so a literal
 *  compare would refuse an attach that names the worktree it already has. */
function samePath(a: string, b: string): boolean {
  const real = (p: string): string => {
    try {
      return realpathSync(p)
    } catch {
      return resolve(p)
    }
  }
  return real(a) === real(b)
}

/** Explain why a finished workspace cannot be attached. */
export function finishedWorkspaceMessage(id: string, view: RemoteWorkspaceView): string {
  const workspace = view.workspace
  if (workspace.status === 'landed') {
    return (
      `Workspace ${id} landed at ${workspace.landedOid?.slice(0, 10) ?? 'an unrecorded commit'} and its ref was deleted — ` +
      `its whole line is reachable through that merge commit (\`git log ${workspace.landedOid ?? '<oid>'}\`). ` +
      `Attach only works on active workspaces; start a new one for follow-up work.`
    )
  }
  return (
    `Workspace ${id} is ${workspace.status} — its ref is retained briefly (fetch ${workspace.ref} to inspect), ` +
    `but attach only works on active workspaces.`
  )
}

/**
 * Reap a failed fresh attach only when the target still contains nothing but
 * the repository metadata this command created. Any checkout file or
 * concurrent write makes the directory user-owned and therefore untouchable.
 */
export function cleanFailedFreshAttachDir(dir: string): string[] {
  if (!existsSync(dir)) return []
  let entries: string[]
  try {
    entries = readdirSync(dir)
  } catch {
    return ['<unreadable>']
  }
  if (entries.length > 0 && !(entries.length === 1 && entries[0] === '.git')) {
    return entries
  }
  try {
    rmSync(dir, { recursive: true, force: true })
    return []
  } catch {
    return entries.length > 0 ? entries : ['<cleanup-failed>']
  }
}

export const attachWorkspaceCommand = defineDeepspaceCommand({
  meta: {
    name: 'attach',
    description: 'Resume a workspace here (another machine or a fresh sandbox)',
  },
  args: {
    id: { type: 'positional', description: 'Workspace id (ws_…)', required: true },
    dir: {
      type: 'positional',
      description: 'Directory (worktree or fresh clone target)',
      required: false,
    },
    app: APP_ARG,
  },
  async run({ args }) {
    const id = String(args.id).trim()
    if (!isWorkspaceId(id)) {
      throw new Refusal(`Invalid workspace id: ${id} (expected ws_<ULID>).`, 'invalid_workspace')
    }
    const branch = workspaceBranchName(id)
    const insideRepo = findAppDir()
    const appArg = typeof args.app === 'string' ? args.app : undefined
    const { app: appSelector, error: appError } = parseAppArg(appArg)
    if (appError) throw new Refusal(appError, 'invalid_app')
    if (!insideRepo && appSelector === undefined) {
      throw new Refusal(
        'Not inside an app repo — pass the app explicitly: deepspace workspace attach <ws_id> [dir] -a <app id or name>.',
        'not_in_app_repo',
      )
    }

    const spinner = args.json ? null : createSpinner()
    spinner?.start(`Preparing to attach ${id}…`)

    if (insideRepo) {
      const { appDir, appId, api, token } = await resolveTarget(appArg)
      spinner?.message(`Attaching ${id}…`)
      const { view } = await api.getWorkspace(id)
      if (view.workspace.status !== 'active') {
        spinner?.stop('Workspace finished.')
        throw new Refusal(finishedWorkspaceMessage(id, view), 'workspace_not_active', {
          extra: { status: view.workspace.status },
        })
      }
      // The checkout (and every worktree sharing its .git) is about to commit
      // on this line, so the token goes in: ensureSpaceRemote gives it an
      // identity too.
      ensureSpaceRemote(appDir, appId, undefined, token)
      const localWorkspaceRef = spacePrivateRef(`workspaces/${id}`)
      runGitRemote(appDir, token, [
        'fetch',
        '--quiet',
        SPACE_REMOTE,
        `+${view.workspace.ref}:${localWorkspaceRef}`,
      ])
      const tip = view.tipOid ?? view.workspace.baseOid
      if (!resolveCommit(appDir, tip)) {
        spinner?.stop('Fetch failed.')
        throw new Refusal(
          `Could not fetch ${view.workspace.ref} from the cloud repo. Retry; if it persists, report it with \`deepspace feedback\`.`,
          'fetch_failed',
        )
      }
      const requestedDir = typeof args.dir === 'string' ? args.dir.trim() : ''
      const worktreeRoot = requestedDir
        ? isAbsolute(requestedDir)
          ? requestedDir
          : resolve(appDir, requestedDir)
        : defaultWorkspaceRoot(appDir, id)
      const dir = appDirInWorktree(appDir, worktreeRoot)
      const localTip = resolveCommit(appDir, `refs/heads/${branch}`)
      if (localTip) {
        // Attach is a "get me this workspace's checkout" verb — when the
        // checkout already exists, pointing at it IS success. Refusing here
        // dead-ended every stale `workspace attach` action another verb had
        // emitted before the worktree existed. `git worktree list` keeps
        // listing an rm -rf'd worktree (prunable) — verify the directory is
        // real before claiming it, and prune so re-materializing can work.
        let existing = resolveWorkspaceWorktree(listWorktrees(appDir), id)
        if (existing && !existsSync(existing)) {
          runGit(appDir, ['worktree', 'prune'], { allowFail: true })
          existing = null
        }
        // The checkout can trail or diverge from the published tip, and the
        // relation decides the one recovery that actually works from there:
        // `sync` publishes when ahead, but deterministically refuses a
        // behind/diverged line — that one needs the pull first.
        const relation = workspaceSyncRelation(appDir, localTip, tip)
        const inSync = relation === 'in_sync'
        const behindOrDiverged = relation === 'behind' || relation === 'diverged'
        const syncAdvice = behindOrDiverged
          ? `integrate the published tip first (\`git pull --no-rebase ${SPACE_REMOTE} ${view.workspace.ref}\`)`
          : `run \`deepspace workspace sync\` from it`
        const syncAction = (cwd: string): CliAction =>
          behindOrDiverged
            ? { cwd, argv: ['git', 'pull', '--no-rebase', SPACE_REMOTE, view.workspace.ref] }
            : { cwd, argv: ['deepspace', 'workspace', 'sync'] }
        if (existing && (!requestedDir || samePath(existing, worktreeRoot))) {
          const existingDir = appDirInWorktree(appDir, existing)
          // The truthful tip is the WORKTREE's, not the server's: this verb
          // moved nothing, and reporting a remote tip that is not checked
          // out anywhere here would send an agent to judge the wrong tree.
          spinner?.stop(`Already attached: ${id}.`)
          if (!args.json) {
            p.log.info(`Task: ${view.workspace.task}`)
            p.log.info(`Work in: cd ${existingDir}`)
            if (!inSync) {
              p.log.warn(
                `The checkout is at ${localTip.slice(0, 10)} but the workspace's published tip is ${tip.slice(0, 10)} — ${syncAdvice}.`,
              )
            }
          }
          return {
            data: {
              workspaceId: id,
              mode: 'worktree',
              dir: existingDir,
              worktreeRoot: existing,
              branch,
              tipOid: localTip,
              remoteTip: tip,
              syncRelation: relation,
              task: view.workspace.task,
              alreadyAttached: true,
            },
            ...(inSync ? {} : { action: syncAction(existingDir) }),
          }
        }
        if (!existing) {
          // Branch present, checkout gone — what `git worktree remove` (or a
          // failed cleanup) leaves behind. The branch is this workspace's own
          // line, so re-materialize it; the fast-forward publish rule already
          // protects any unpublished commits it carries.
          const failure = rematerializeWorkspaceWorktree(appDir, worktreeRoot, branch, id)
          if (failure) {
            spinner?.stop('Branch exists.')
            // The usual cause is git's own stale worktree registration (a
            // hand-deleted directory still registered) — git's embedded error
            // even names `git worktree prune`; hand it back as the action
            // instead of "check it out yourself" (r2 workspaces AX F5, where
            // prune-then-retry was the verified one-step fix).
            throw new Refusal(
              `Branch ${branch} exists locally with no worktree, and re-materializing it failed: ${failure}. If a deleted worktree is still registered, prune it and re-run; otherwise inspect the branch (\`git log ${branch}\`) and check it out yourself.`,
              'branch_exists',
              { action: { cwd: appDir, argv: ['git', 'worktree', 'prune'] } },
            )
          }
          spinner?.stop(`Re-attached ${id}.`)
          if (!args.json) {
            p.log.info(`Task: ${view.workspace.task}`)
            p.log.info(`Work in: cd ${dir}`)
            if (!inSync) {
              p.log.warn(
                `The re-materialized checkout is at ${localTip.slice(0, 10)} but the workspace's published tip is ${tip.slice(0, 10)} — after installing, ${syncAdvice}.`,
              )
            }
          }
          const action: CliAction = { cwd: dir, argv: [detectPackageManager(dir), 'install'] }
          return {
            data: {
              workspaceId: id,
              mode: 'worktree',
              dir,
              worktreeRoot,
              branch,
              tipOid: localTip,
              remoteTip: tip,
              syncRelation: relation,
              task: view.workspace.task,
              rematerialized: true,
            },
            action,
          }
        }
        spinner?.stop('Branch exists.')
        throw new Refusal(
          `Workspace ${id} is already checked out at ${existing} — continue there (or drop that worktree before attaching it elsewhere).`,
          'branch_exists',
        )
      }
      materializeWorkspaceWorktree(appDir, worktreeRoot, branch, tip, id)
      spinner?.stop(`Attached ${id}.`)
      if (!args.json) {
        p.log.info(`Task: ${view.workspace.task}`)
        p.log.info(`Work in: cd ${dir}`)
      }
      const action: CliAction = { cwd: dir, argv: [detectPackageManager(dir), 'install'] }
      return {
        data: {
          workspaceId: id,
          mode: 'worktree',
          dir,
          worktreeRoot,
          branch,
          tipOid: tip,
          remoteTip: tip,
          // Freshly materialized at the published tip — trivially in sync;
          // every attach outcome carries the same shape.
          syncRelation: 'in_sync',
          task: view.workspace.task,
        },
        action,
      }
    }

    // Fresh-clone mode materializes only this workspace's hidden ref.
    const token = await ensureToken()
    const appId = await resolveAppTarget(deployBaseUrl(), token, appSelector)
    const api = repoApi(deployBaseUrl(), token, appId)
    spinner?.message(`Attaching ${id} into a fresh clone…`)
    const { view } = await api.getWorkspace(id)
    if (view.workspace.status !== 'active') {
      spinner?.stop('Workspace finished.')
      throw new Refusal(finishedWorkspaceMessage(id, view), 'workspace_not_active', {
        extra: { status: view.workspace.status },
      })
    }
    const dir = resolve(
      process.cwd(),
      (typeof args.dir === 'string' ? args.dir.trim() : '') || `ws-${id.slice(3).toLowerCase()}`,
    )
    if (existsSync(dir)) {
      spinner?.stop('Target exists.')
      throw new Refusal(`${dir} already exists — pass a different directory.`, 'dir_exists')
    }
    const failFreshAttach = (message: string, code?: string): never => {
      const leftovers = cleanFailedFreshAttachDir(dir)
      const cleanupNote =
        leftovers.length > 0
          ? ` The failed attach left ${dir} in place because it contains: ${leftovers.join(', ')}.`
          : ''
      throw new Refusal(message + cleanupNote, code ?? 'attach_failed', {
        extra: leftovers.length > 0 ? { leftovers } : undefined,
      })
    }
    let tip: string
    try {
      runGit(process.cwd(), ['init', '--quiet', dir])
      // A brand-new repo the agent is about to commit in, so the token goes
      // in: ensureSpaceRemote gives it an identity too.
      ensureSpaceRemote(dir, appId, undefined, token)
      const localWorkspaceRef = spacePrivateRef(`workspaces/${id}`)
      runGitRemote(dir, token, [
        'fetch',
        '--quiet',
        SPACE_REMOTE,
        `+${view.workspace.ref}:${localWorkspaceRef}`,
      ])
      tip = view.tipOid ?? view.workspace.baseOid
      if (!resolveCommit(dir, tip)) {
        spinner?.stop('Fetch failed.')
        return failFreshAttach(
          `Could not fetch ${view.workspace.ref} from the cloud repo. Retry; if it persists, report it with \`deepspace feedback\`.`,
          'fetch_failed',
        )
      }
      switchToNewBranch(dir, branch, tip)
    } catch (error) {
      if (error instanceof Refusal) throw error
      return failFreshAttach(
        error instanceof Error ? error.message : String(error),
        errorCode(error),
      )
    }
    spinner?.stop(`Attached ${id}.`)
    const action: CliAction = { cwd: dir, argv: [detectPackageManager(dir), 'install'] }
    if (!args.json) {
      p.log.info(`Task: ${view.workspace.task}`)
      p.log.info('Pull app secrets after install if this workspace needs them.')
    }
    return {
      data: {
        workspaceId: id,
        mode: 'clone',
        dir,
        branch,
        tipOid: tip,
        remoteTip: tip,
        syncRelation: 'in_sync',
        task: view.workspace.task,
      },
      action,
    }
  },
})
